'use client';

import { useState, useEffect, useCallback } from 'react';
import { CACHE_QUERY_KEY } from 'app/constants';
import { useModal, useToast } from '@shared/ui';
import { ulid } from 'ulid';
import { useEffectOnce } from 'libs/useEffectOnce';
import { mutate } from 'swr';
import { ValidationError, ERROR_CODE } from '@shared/services';
import { useCartCalculate, useCartOrderGiveaway, useCartShipping } from 'app/cart/core';
import {
    putCart,
    deleteCart,
    getPaymentMethod,
    getCartAutoFill,
    getCartCount,
} from 'app/apis/cart';
import { getProductCard, PRODUCT_GROUP_SOURCE_TYPE } from 'app/apis/product';
import { TRACK_EVENT, logEvent } from 'app/libs/track';
import { postOrder } from 'app/apis/order';
import { getAddressCountry } from 'app/apis/address';
import i18n from '@shared/i18n';
import { useRouter } from 'next/navigation';
import { type CartProductCardType } from 'app/components/ProductCard';
import * as Sentry from '@sentry/nextjs';

export interface UseCartProps {}

const INITIAL_INVOICE_INFO: InvoiceInfo = {
    mobile_barcode: null,
    natural_id: null,
    tax_id: null,
    tax_title: null,
    love_code: null,
    type: null,
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useCart(_props: UseCartProps) {
    const toast = useToast();

    const router = useRouter();

    const { modal } = useModal();

    const [couponId, setCouponId] = useState<string | null>(null);

    const [paymentMethod, setPaymentMethod] = useState<CartPaymentMethod[] | null>(null);

    const [paymentType, setPaymentType] = useState('');

    const [cardInfo, setCardInfo] = useState<CardInfo>({
        card_no: '',
        cvv: '',
        expiry: '',
    });

    const [isCardInfoValid, setIsCardInfoValid] = useState(false);

    const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo>(INITIAL_INVOICE_INFO);

    const [invoiceType, setInvoiceType] = useState<InvoiceTypeOption>();

    const [buyerRemark, setBuyerRemark] = useState<string | null>(null);

    const [recommendCartItems, setRecommendCartItems] = useState<ProductCard[]>([]);

    const [hasItems, setHasItems] = useState(false);

    function onCartCalculateError() {
        setCouponId(null);
    }

    const fetchCartCount = async (): Promise<number> => {
        try {
            const res = await getCartCount();

            if (res?.data) {
                return res.data.count;
            }

            return 0;
        } catch (error) {
            console.error(error);

            return 0;
        }
    };

    const {
        state: {
            shippingMethod,
            completedAddressBook,
            shippingProfileId,
            shippingType,
            shipTo,
            shipFrom,
            addressForm,
            isReceiverAsBuyer,
            isCalculating: isCartShippingCalculating,
            addressCountry,
            error: addressFormError,
            completedAddress,
            editingAddress,
            draftAddressBook,
        },
        actions: {
            updateAddressFormFieldValue,
            updateShippingType,
            updateShipTo,
            updateShipFrom,
            fetchAddressForm,
            updateIsReceiverAsBuyer,
            applyAddress,
            registerFieldElement,
            getFieldElement,
            updateAddressCountry,
            resetError: resetAddressFormError,
            updateEditingAddress,
            syncCompletedToDraft,
            updateShippingProfileId,
            updateAddressBook,
        },
    } = useCartShipping();

    const {
        state: {
            isFetching,
            originalCartItems,
            completedCartItems,
            completedShoppingDetail,
            isCalculating: isCartCalculating,
        },
        actions: {
            setCalculating: setCartCalculating,
            mutate: mutateCartCalculate,
            setIsFetching,
        },
    } = useCartCalculate({
        subdivision: completedAddressBook?.subdivision,
        shipping_profile_id: shippingProfileId,
        coupon_id: couponId,
        hasItems,
        onError: onCartCalculateError,
        setHasItems,
    });

    const {
        state: {
            hasOrderGiveaway,
            orderGiveaway,
            isCalculating: isCartOrderGiveawayCalculating,
        },
        actions: { setCalculating: setCartOrderGiveawayCalculating },
    } = useCartOrderGiveaway({
        product_total_price:
            Number(completedShoppingDetail?.product_total_price?.amount) || 0,
        promo_reduce_price:
            Number(completedShoppingDetail?.promo_reduce_price?.amount) || 0,
        coupon_reduce_price:
            Number(completedShoppingDetail?.coupon_reduce_price?.amount) || 0,
        hasItems,
    });

    const fetchRecommendCartItems = useCallback(
        async (_completedCartItems: CartProductCardType[]) => {
            try {
                const res = await getProductCard({
                    type: PRODUCT_GROUP_SOURCE_TYPE.RECOMMEND,
                    query: _completedCartItems.map((item) => item.productId).join(','),
                });

                if (res?.data) {
                    setRecommendCartItems(res.data);
                }
            } catch (error) {
                console.error(error);
            }
        },
        [setRecommendCartItems]
    );

    async function presetShippingCart() {
        // 進購物車須先執行的 api call
        // 先檢查購物車數量
        const cartCount = await fetchCartCount();

        if (!cartCount) {
            setIsFetching(false);

            return;
        }

        setHasItems(!!cartCount);

        const [cartAutoFillResp, addressCountryResp] = await Promise.allSettled([
            // 取得前筆訂單資料進行預填
            getCartAutoFill(),
            // 取得地址國家資料
            getAddressCountry(),
        ]);

        if (cartAutoFillResp.status === 'fulfilled' && cartAutoFillResp?.value?.data) {
            const {
                shipping_profile_id,
                shipping_type,
                address_book,
                payment_type,
                invoice_type_display,
                invoice_info,
            } = cartAutoFillResp.value.data;

            updateShippingProfileId(shipping_profile_id);

            updateAddressBook(address_book);

            updateShipTo(address_book?.ship_to || shipTo);

            updateShipFrom(address_book?.ship_from || shipFrom);

            updateShippingType(shipping_type);

            if (invoice_type_display) {
                setInvoiceType(invoice_type_display);
            }

            if (invoice_info) {
                setInvoiceInfo(invoice_info);
            }

            setPaymentType(payment_type);
        }

        if (
            addressCountryResp.status === 'fulfilled' &&
            addressCountryResp?.value?.data
        ) {
            updateAddressCountry(
                addressCountryResp.value.data.map((item) => ({
                    label: item.key,
                    value: item.value,
                }))
            );
        }

        // TODO: 錯誤處理的細節要補上
        if (cartAutoFillResp.status === 'rejected') {
            if (
                cartAutoFillResp.reason instanceof ValidationError &&
                cartAutoFillResp.reason.context.error_code === ERROR_CODE.DATA_NOT_FOUND
            ) {
                modal({
                    headerContent: i18n.gettext('賣家尚未設定物流'),
                    bodyContent: i18n.gettext('賣家尚未設定物流'),
                    showCloseButton: false,
                    showCancelButton: false,
                    onRespond: () => {
                        setIsFetching(false);
                    },
                });
            } else {
                console.error(cartAutoFillResp.reason);
            }
        }
    }

    async function fetchPaymentMethod(
        _shippingType: ShippingType,
        grandTotalPrice?: string
    ) {
        if (!_shippingType || !grandTotalPrice) {
            return;
        }

        try {
            const resp = await getPaymentMethod({
                shipping_type: _shippingType,
                grand_total_price: Number(grandTotalPrice),
            });

            if (resp?.data) {
                setPaymentMethod(resp.data);
                setPaymentType((prev) => {
                    if (resp.data.find((item) => item.payment_type === prev)) {
                        return prev;
                    }

                    return resp.data[0].payment_type;
                });
            }
        } catch (error) {
            // TODO: 錯誤處理
            console.error(error);
        }
    }

    useEffectOnce(() => {
        // auto fill
        presetShippingCart();
    });

    const isCheckoutButtonDisabled = () => {
        const _isCreditCard = paymentType === 'credit_card';
        const _isCardInfoValid = _isCreditCard
            ? !!(cardInfo?.card_no && cardInfo?.cvv && cardInfo?.expiry) &&
              isCardInfoValid
            : true;
        const _isToastMessagesEmpty = !completedShoppingDetail?.toast_messages?.length;

        return !(
            shippingType &&
            completedAddressBook &&
            paymentType &&
            invoiceType &&
            completedShoppingDetail &&
            shippingProfileId &&
            _isCardInfoValid &&
            _isToastMessagesEmpty
        );
    };

    function setCalculating(isCalculating: boolean) {
        setCartCalculating(isCalculating);
        setCartOrderGiveawayCalculating(isCalculating);
    }

    async function calculate<T = unknown>(action: () => Promise<T | null>) {
        setCalculating(true);

        const resp = await action();

        try {
            await mutateCartCalculate();

            return resp;
        } finally {
            setCalculating(false);
        }
    }

    async function adjustCartItemQuantity(cartId: string, quantity: number) {
        return calculate(async () => {
            return putCart({ quantity }, cartId);
        });
    }

    async function deleteCartItem(cartId: string) {
        return calculate<BasicSuccessResponse>(async () => {
            return deleteCart({ cart_id: cartId }).finally(() => {
                mutate(CACHE_QUERY_KEY.buyerCartCount);
            });
        });
    }

    function handleCouponIdOnChange(id: string | null) {
        setCouponId(id);
    }

    function handleCardInfoOnChange(key: keyof CardInfo, value: string) {
        setCardInfo((prev) => ({ ...prev, [key]: value }));
    }

    function handleInvoiceInfoOnChange(newInvoiceInfo?: Partial<InvoiceInfo>) {
        setInvoiceInfo({ ...INITIAL_INVOICE_INFO, ...newInvoiceInfo });
    }

    function handleInvoiceTypeOnChange(newInvoiceType?: InvoiceTypeOption) {
        setInvoiceType(newInvoiceType);
    }

    function handleBuyerRemarkOnChange(newBuyerRemark: string) {
        setBuyerRemark(newBuyerRemark);
    }

    async function checkout() {
        if (!completedShoppingDetail?.can_checkout) {
            toast({
                title: i18n.gettext('購物車內有商品已失效，請刪除或調整數量'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });

            return;
        }

        if (!invoiceType || !shippingProfileId) {
            return;
        }

        const payload: PostCartCreateOrderPayload = {
            order_id: ulid(),
            idempotency_key: ulid(),
            shipping_type: shippingType,
            address_book: completedAddressBook as CartAddressBook,
            payment_type: paymentType,
            card_info: cardInfo,
            coupon_id: couponId,
            buyer_remark: buyerRemark,
            invoice_type: invoiceType.type,
            shipping_profile_id: shippingProfileId,
            grand_total_price: Number(completedShoppingDetail?.grand_total_price?.amount),
            product_total_price: Number(
                completedShoppingDetail?.product_total_price?.amount
            ),
            promo_reduce_price: Number(
                completedShoppingDetail?.promo_reduce_price?.amount
            ),
            coupon_reduce_price: Number(
                completedShoppingDetail?.coupon_reduce_price?.amount
            ),
            shipping_fee: Number(completedShoppingDetail?.shipping_fee?.amount),
            shipping_fee_reduce_price: Number(
                completedShoppingDetail?.shipping_fee_reduce_price?.amount
            ),
        };

        try {
            const resp = await postOrder(payload);

            if (resp?.data) {
                if (resp.data.html) {
                    const tempDiv = document.createElement('div');

                    tempDiv.innerHTML = resp.data.html;
                    document.body.appendChild(tempDiv);

                    const form = tempDiv.querySelector('form');

                    form?.submit();
                } else if (resp.data.next_url) {
                    router.replace(resp.data.next_url);
                }

                logEvent(TRACK_EVENT.BEGIN_CHECKOUT, {
                    event: TRACK_EVENT.BEGIN_CHECKOUT,
                });
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { scope: 'Client' },
                extra: { payload },
            });
        } finally {
            setHasItems(false);
            mutateCartCalculate();
        }
    }

    // 當 couponCode 或 shippingMethod 改變時，自動執行 calculate
    useEffect(() => {
        mutate(CACHE_QUERY_KEY.calculate);
    }, [couponId, mutate]);

    useEffect(() => {
        fetchPaymentMethod(
            shippingType,
            completedShoppingDetail?.grand_total_price?.amount
        );
    }, [shippingType, completedShoppingDetail]);

    useEffect(() => {
        if (completedCartItems) {
            fetchRecommendCartItems(completedCartItems);
        }
    }, [completedCartItems, fetchRecommendCartItems]);

    const state = {
        // 商品卡相關
        originalCartItems,
        completedCartItems,
        recommendCartItems,

        // 訂單加購品
        hasOrderGiveaway,
        orderGiveaway,
        isCartOrderGiveawayCalculating,

        // address & shipping 相關
        addressForm,
        shippingType,
        shipTo,
        shipFrom,
        shippingProfileId,
        isCartShippingCalculating,
        draftAddressBook,
        completedAddressBook,
        shippingMethod,
        isReceiverAsBuyer,
        addressFormError,
        addressCountry,
        completedAddress,
        editingAddress,

        // 付款方式
        paymentMethod,
        paymentType,
        cardInfo,
        isCardInfoValid,
        // 發票資料
        invoiceType,
        invoiceInfo,

        // 訂單備註
        buyerRemark,

        // 優惠券
        couponId,

        // 小計
        completedShoppingDetail,
        isCartCalculating,

        // 初始化中
        isFetching,
    };

    const actions = {
        calculate,
        adjustCartItemQuantity,
        deleteCartItem,
        setPaymentType,
        setIsCardInfoValid,
        handleCouponIdOnChange,
        handleCardInfoOnChange,
        handleInvoiceInfoOnChange,
        handleInvoiceTypeOnChange,
        handleBuyerRemarkOnChange,
        updateShippingType,
        updateShipTo,
        updateShipFrom,
        fetchAddressForm,
        updateAddressFormFieldValue,
        updateIsReceiverAsBuyer,
        applyAddress,
        registerFieldElement,
        getFieldElement,
        updateEditingAddress,
        resetAddressFormError,
        syncCompletedToDraft,
        checkout,
        isCheckoutButtonDisabled,
    };

    return {
        state,
        actions,
    };
}

export type UseCartReturn = ReturnType<typeof useCart>;
